import { useEffect, useState } from 'react';
import { apiGet, apiPatch } from '../lib/api';
import { useAuth, useFeature } from '../context/AuthContext';
import { isDtStaff } from '../lib/staff';
import type { TicketDetail } from '../types';

/**
 * Changement du déclarant par un CLIENT (interrupteur `ticket_edit_client`) :
 * un simple choix parmi les utilisateurs actifs de sa société. Pas sur un
 * point Finalisé. Les agents Digital Team passent par « Modifier le point ».
 */
export function DeclarantEditor({ ticket, onChanged }: { ticket: TicketDetail; onChanged: () => void }) {
  const { user } = useAuth();
  const open = useFeature('ticket_edit_client');
  // Compte restreint à ses propres points (self_only) : refusé par l'API, masqué ici.
  const visible = open && !isDtStaff(user) && !user?.self_only && ticket.etat !== 'Finalisé';
  const [users, setUsers] = useState<{ id_user: number; nom_user: string }[]>([]);
  const [choice, setChoice] = useState(ticket.id_user);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);

  useEffect(() => setChoice(ticket.id_user), [ticket.id_user]);
  useEffect(() => {
    if (!visible) return;
    const ctl = new AbortController();
    apiGet<{ items: { id_user: number; nom_user: string }[] }>(`/api/tickets/${ticket.display_id}/declarants`, ctl.signal)
      .then((r) => setUsers(r.items))
      .catch(() => {});
    return () => ctl.abort();
  }, [visible, ticket.display_id]);

  if (!visible || users.length < 2) return null;

  async function save() {
    setMsg(null);
    setBusy(true);
    try {
      await apiPatch(`/api/tickets/${ticket.display_id}/declarant`, { id_user: choice });
      setMsg({ ok: 'Déclarant modifié' });
      onChanged();
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 8, fontSize: 'var(--fs-xs)' }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 4 }}>
        Déclarant
        <select
          value={choice}
          onChange={(e) => setChoice(Number(e.target.value))}
          style={{ display: 'block', width: '100%', marginTop: 2, padding: 5, fontSize: 'var(--fs-xs)', border: '1px solid var(--border-strong)', borderRadius: 5, background: 'var(--bg)', color: 'var(--text)' }}
        >
          {!users.some((u) => u.id_user === ticket.id_user) && <option value={ticket.id_user}>{ticket.posteur_nom}</option>}
          {users.map((u) => (
            <option key={u.id_user} value={u.id_user}>
              {u.nom_user}
            </option>
          ))}
        </select>
      </label>
      <button type="button" disabled={busy || choice === ticket.id_user} onClick={save} style={{ fontSize: 'var(--fs-xs)', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>
        Changer le déclarant
      </button>
      {msg?.ok && <span style={{ marginLeft: 8, color: 'var(--ok, #15803d)' }}>{msg.ok}</span>}
      {msg?.err && <span style={{ marginLeft: 8, color: 'var(--danger, #b91c1c)' }}>{msg.err}</span>}
    </div>
  );
}
